import { useEffect } from "react";
import { App as CapacitorApp, type URLOpenListenerEvent } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { router } from "./routing";
import { useAuth } from "./contexts/AuthContext";

/**
 * Android App Link return handler (Ирина's decision, 29.09: payment must return the
 * user INTO the app, not leave them in the system browser).
 *
 * T-Bank checkout deliberately opens in the system browser (see
 * docs/mobile-capacitor-wrapper.md, "(в) Payment" — unchanged here, out of scope).
 * The returnUrl it redirects back to is https://forruss.ru/payment/confirm
 * (paymentsService.ts's resolveMobilePaymentReturnUrl). The App Links intent-filter in
 * AndroidManifest.xml (autoVerify, scoped to that one path) makes Android hand that URL
 * to this app instead of loading it as a page in the browser — MainActivity's
 * singleTask launchMode routes it into the already-running Activity's onNewIntent, and
 * Capacitor's Bridge turns that into an "appUrlOpen" event carrying the full URL.
 *
 * That event only reaches the WebView/JS layer — it does not by itself navigate the SPA
 * router (the WebView's own document is still whatever screen was open when the user
 * left for checkout, e.g. the landing page or /app), so this listener does that
 * explicitly: pull the path+query off the incoming URL and hand it to the router.
 *
 * It also refreshes the auth/entitlement snapshot (refreshUser → GET current user):
 * the purchase itself was already applied server-side by T-Bank's webhook
 * (backend/app/api/payments_routes.py, independent of anything the client does), so by
 * the time this fires the entitlement is already correct in the database — this just
 * makes sure the app's in-memory user state catches up without waiting for whatever
 * next fetches it naturally. PaymentConfirmPage itself separately polls the specific
 * payment's own status (getPayment) to render paid/pending/failed.
 */
export function CapacitorDeepLinkHandler() {
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listenerHandle = CapacitorApp.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      let target: string;
      try {
        const url = new URL(event.url);
        target = `${url.pathname}${url.search}${url.hash}`;
      } catch {
        // Не URL — просто игнорируем, переходить некуда.
        return;
      }

      router.navigate(target);
      void refreshUser();
    });

    return () => {
      void listenerHandle.then((handle) => handle.remove());
    };
  }, [refreshUser]);

  return null;
}
